import { logger } from '@adornis/base/logging.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { GlobalSetting } from '@adornis/buchungsheld-db/entities/global-setting.js';
import { GlobalMailTemplate } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/file-utils/client/x-image.js';
import '@adornis/mails/client/x-mails-template-editor.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map, of, switchMap } from 'rxjs';
import { BHFormController } from '../../../controller/BHFormController.js';

/**
 * @element bh-page-admin-global-settings-mails
 */
@customElement('bh-page-admin-global-settings-mails')
export class BHPageAdminGlobalSettingsMails extends BuchungsheldLitElement {
  @state() private readonly _mailTemplates = new RXController(
    this,
    GlobalMailTemplate.getGlobalMailTemplates()(GlobalMailTemplate.allFields),
  );

  @state() private readonly _globalSettings = new RXController(
    this,
    of(true).pipe(
      switchMap(() => GlobalSetting.getGlobalSetting()({ _id: 1, emailBannerImageID: 1 })),
      map(settings => settings || null),
    ),
  );

  @state() private readonly _globalSettingsController = new BHFormController(this, this._globalSettings.observable, {
    success: async globalSettings => {
      try {
        await XDialog.waitFor(globalSettings.save());
        XDialog.alert(this._translation.translate('settings', 'success_mail_banner'));
      } catch (err) {
        logger.error({ err }, 'Problem updating global mail banner image');
        XDialog.alert(this._translation.translate('settings', 'err_mail_banner'));
      }
    },
  });

  override render() {
    return html`
      <x-flex space="xl">
        <bh-breadcrumb
          .items=${[
            { content: this._translation.translate('settings', 'name'), href: '/admin/settings' },
            { content: this._translation.translate('mail_templates'), active: true },
          ]}
        ></bh-breadcrumb>
        <x-flex space="lg">
          <!-- Mail Template Editor Section -->
          <x-text ${css({ maxWidth: this.sizes.maxReadableWidth })}>
            ${this._translation.translate('global_settings', 'email_hint')}
          </x-text>
          ${this._mailTemplates.isLoading
            ? html` <x-flex flex center crossaxis-center> <tr-loader></tr-loader> </x-flex> `
            : html`
                <x-mails-template-editor
                  .getMailTemplates=${() => this._mailTemplates.value ?? []}
                ></x-mails-template-editor>
              `}

          <!-- Email Banner Image Upload Section -->
          <tr-card ${css({ width: '100%' })}>
            <x-flex space="sm">
              <tr-text> ${this._translation.translate('settings', 'config_mail_banner_image')} </tr-text>
              <tr-text tone="subtle"> ${this._translation.translate('settings', 'upload_mail_banner_image')} </tr-text>
            </x-flex>
            <x-image
              ${this._globalSettingsController.field('emailBannerImageID')}
              mode="contain"
              ${css({ width: '100%', height: '250px' })}
            ></x-image>
            <tr-button
              icon="save"
              ${css({ alignSelf: 'flex-end' })}
              @click=${e => this._globalSettingsController.validate({ mouseEvent: e })}
            >
              ${this._translation.translate('save')}
            </tr-button>
          </tr-card>
        </x-flex>
      </x-flex>
    `;
  }
}
